import { useCallback, useEffect, useRef, useState } from "react";
import PublicationCard from "../../components/PublicationCard";
import fallbackImage from "../../assets/admin.png";
import ClipLoader from "react-spinners/ClipLoader";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "react-toastify";
import DropdownActions from "../../components/DropdownActions";
import axios from "axios";
const BASE_URL = process.env.REACT_APP_BASE_URL;
const isImageFile = (filename = "") => /\.(jpg|jpeg|png|gif)$/i.test(filename);
const isPdfFile = (filename = "") => /\.pdf$/i.test(filename);
const getFullUrl = (path = "") => path?.startsWith("http") ? path : `${BASE_URL}${path}`;

const Publication = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editPublicationId, setEditPublicationId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editAbstract, setEditAbstract] = useState("");
  const [deletePublicationId, setDeletePublicationId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const initialUserId = localStorage.getItem("user_id");
  const [currentUserId, setCurrentUserId] = useState(initialUserId);
  const [editVisibility, setEditVisibility] = useState("public");
  const [followingList, setFollowingList] = useState([]);
  const [invitePublicationId, setInvitePublicationId] = useState(null);
  const [collaborators, setCollaborators] = useState({});
  const [receivedInvitations, setReceivedInvitations] = useState([]);
  const { userId: id } = useParams();
  const [previewFile, setPreviewFile] = useState(null);
  const token = localStorage.getItem("access_token");

  const canUserEdit = (pub) => {
    if (!currentUserId) return false;

    if (Number(pub.user) === Number(currentUserId)) return true;

    return receivedInvitations.some(
      (inv) =>
        inv.publication === pub.id &&
        Number(inv.user) === Number(currentUserId) &&
        inv.can_edit
    );
  };

  const canRemoveCollaborator = (pub) => {
    if (!currentUserId) return false;
    return Number(pub.user) === Number(currentUserId);
  };

  const handleEditClick = (pub) => {
    setEditMode(true);
    setEditPublicationId(pub.id);
    setEditTitle(pub.title);
    setEditAbstract(pub.abstract || "");
    setEditVisibility(pub.visibility || "public"); // <-- NEW
  };

  const handleDeleteClick = (pubId) => {
    setDeletePublicationId(pubId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const res = await fetch(`${BASE_URL}/user/publication/publication/`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
        body: JSON.stringify({ id: deletePublicationId }),
      });

      if (res.status === 204) {
        toast.success("Publication deleted successfully!");
        setShowDeleteModal(false);
        setDeletePublicationId(null);

        queryClient.setQueryData(["publications"], (oldData) => {
          if (!oldData) return oldData;
          return {
            pageParams: oldData.pageParams,
            pages: oldData.pages.map((page) => ({
              ...page,
              images: page.images.filter((p) => p.id !== deletePublicationId),
            })),
          };
        });
        return;
      }

      const data = await res.json();

      if (res.ok) {
        toast.success("Publication deleted successfully!");
        setShowDeleteModal(false);
        setDeletePublicationId(null);
        queryClient.setQueryData(["publications"], (oldData) => {
          if (!oldData) return oldData;
          return {
            pageParams: oldData.pageParams,
            pages: oldData.pages.map((page) => ({
              ...page,
              images: page.images.filter((p) => p.id !== deletePublicationId),
            })),
          };
        });
      } else {
        toast.error(data?.message || "Failed to delete publication.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Deletion failed.");
    }
  };

  const handleEditSubmit = async () => {
    console.log("i am here now")
    if (!editTitle.trim()) {
      toast.error("Title cannot be empty");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/user/publication/publication/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
        body: JSON.stringify({
          id: editPublicationId,
          title: editTitle,
          abstract: editAbstract,
          visibility: editVisibility,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        toast.success("Publication updated successfully!");
        setEditMode(false);
        setEditPublicationId(null);
        setEditTitle("");
        setEditAbstract("");
        // console.log("The old people new happy satisfied")
        queryClient.setQueryData(["publications"], (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              images: page.images.map((pub) =>
                pub.id === editPublicationId
                  ? {
                      ...pub,
                      title: editTitle,
                      abstract: editAbstract,
                      visibility: editVisibility,
                    }
                  : pub
              ),
            })),
          };
        });
      } else {
        toast.error(data?.message || "Failed to update publication");
      }
    } catch (error) {
      console.error(error);
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResearch = () => {
    navigate("/publish/journal");
  };

  const fetchCurrentUser = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/user/profile/create-profile/`, {
        headers: { Authorization: `Bearer ${token}`,  "x-api-key": process.env.REACT_APP_X_API_KEY, },
      });
      const data = await res.json();
      if (res.ok) {
        setCurrentUserId(data.data?.user);
        localStorage.setItem("user_id", data.data?.user);
      } else {
        console.error("");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error getting user profile.");
    }
  }, [BASE_URL, token]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const fetchFollowing = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/user/follow-unfollow/follow/`, {
        headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
      });

      if (res.status === 200) {
        return res.data.results.map((item) => item.follow_user_profile);
      }
    } catch (error) {
      console.error("Error fetching following list:", error);
      return [];
    }
  };

  useEffect(() => {
    const loadFollowing = async () => {
      const users = await fetchFollowing();
      setFollowingList(users);
    };
    loadFollowing();
  }, []);

  const fetchPublications = async ({ pageParam = 1 }) => {
    let url;
    if (id) {
      url = `${BASE_URL}/user/profile/all-profiles?id=${id}`;
    } else {
      url = `${BASE_URL}/user/publication/publication/?page=${pageParam}`;
    }

    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
    });

    const data = await res.json();
    console.log("Fetched data:", data);

    if (!res.ok) throw new Error("Failed to fetch publications");

    let publications = [];

    if (id) {
      publications = data.data?.publications || [];
    } else {
      publications = data.results || [];
    }
    publications = publications.map((pub) => ({
      ...pub,
      is_Liked: pub.is_liked ?? false,
      likeCount: pub.total_likes ?? 0,
    }));

    return {
      images: publications,
      count: publications.length,
      currentPage: pageParam,
      next: false,
    };
  };

  const handlePreview = (pub) => {
    navigate("/viewer", {
      state: {
        url: pub.upload_document,
        title: pub.title,
        description: pub.abstract || pub.description,
        type: "publication",
        userId: pub.user,
        postId: pub.id,
        is_Liked: pub.is_liked,
        likeCount: pub.total_likes,
        commentCount: pub.total_comments,
      },
    });
  };

  const handleInviteCollaborator = async (publicationId, collaboratorId) => {
    try {
      const token = localStorage.getItem("access_token");
      const formData = new FormData();
      formData.append("collebrate_user", collaboratorId);
      formData.append("publication_id", publicationId);

      const res = await axios.post(
        `${BASE_URL}/user/publication/add-collebrate-user/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-api-key": process.env.REACT_APP_X_API_KEY
          },
        }
      );
      toast.success(res.data?.message || "Collaborator added successfully!");
          queryClient.invalidateQueries(["publications"]);

    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          "Something went wrong while inviting collaborator."
      );
    }
  };

  const fetchCollaborators = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(
        `${BASE_URL}/user/publication/add-collebrate-user/`,
        {
          headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
        }
      );
       return res.data?.map((c) => ({
      id: c.id,
      user: c.collaborator_user, 
      name: c.name, 
      status: c.status,
      can_edit: c.can_edit,
    })) || [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

 const handleRemoveCollaborator = async (publicationId, collaboratorId) => {
   setCollaborators((prev) => ({
    ...prev,
    [publicationId]: (prev[publicationId] || []).filter(
      (c) => c.id !== collaboratorId
    ),
  }));
  try {
    const token = localStorage.getItem("access_token");

    const res = await axios.delete(
      `${BASE_URL}/user/publication/add-collebrate-user/`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
        data: {
          publication_id: publicationId,
          collaborator_id: collaboratorId,
        },
      }
    );

    if (res.status === 204 || res.data?.success) {
      toast.success("Collaborator removed successfully!");
    } else {
      toast.error(res.data?.message || "Failed to remove collaborator.");
    }
  } catch (err) {
    console.error(err);
    toast.error("Error removing collaborator.");
  }
};


  const fetchReceivedInvitations = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(
        `${BASE_URL}/user/publication/view-received-invitation-list/`,
        {
          headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY  },
        }
      );
      if (res.status === 200) {
        setReceivedInvitations(res.data.data || []);
        console.log("Received invitations:", res.data.data || []);
      }
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setReceivedInvitations([]);
        console.log("No invitations found for this user.");
      } else {
        console.error(err);
        toast.error("Failed to fetch invitations.");
      }
    }
  };

  useEffect(() => {
    fetchReceivedInvitations();
  }, []);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["publications"],
      queryFn: fetchPublications,
      getNextPageParam: (lastPage, pages) => {
        return lastPage.next ? pages.length + 1 : undefined;
      },
      initialPageParam: 1,
    });

  const observerRef = useRef();
  const lastImageRef = useCallback(
    (node) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && hasNextPage) {
            fetchNextPage();
          }
        },
        {
          root: null,
          rootMargin: "100px",
          threshold: 0,
        }
      );

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );
  const imagesData = data?.pages.flatMap((page) => page.images) || [];
  return (
   
  <div className="w-full max-w-[1050px]">
     {!isLoading && (
  <div className="w-full flex justify-between">
    <div >
      <h2 className="text-lg font-bold text-gray-900">
        Publications
      </h2>
      <p className="text-blue-600 font-medium mt-1">
        Articles & Books
      </p>
    </div>
    <div className="flex justify-end mb-5">
      {!id && (
        <button
          className="bg-[#004182] text-white p-3 rounded-full font-medium cursor-pointer border border-[#00449d] flex items-center justify-center"
          onClick={() => handleResearch()}
        >
          <Plus size={18} />
        </button>
      )}
    </div>
  </div>
)}
      <div className="mt-6">
        
        <div>
          {isLoading ? (
            <div className="flex items-center justify-center min-h-[200px]">
              <ClipLoader color="#4A90E2" size={40} />
            </div>
          ) : imagesData.length === 0 ? (
            <p className="flex flex-col justify-center items-center py-12 bg-white rounded-xl border border-gray-200">
  <div className="w-16 h-16 bg-[#e8f0f7] rounded-xl flex items-center justify-center mb-4">
    <svg className="w-8 h-8 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  </div>
  <span className="text-lg font-semibold text-gray-900 mb-2">No publications yet</span>
  <span className="text-gray-500 text-sm">Start adding your articles and books</span>
</p>
          ) : (
            <div className="parent">
              {imagesData.map((pub, index) => {
                if (!pub) return null;
                const fileUrl = getFullUrl(pub.upload_document);
                const fileName =
                  pub.upload_document?.split("/").pop() || "No file";
                const isImage = isImageFile(pub.upload_document);
                const isPdf = isPdfFile(pub.upload_document);
                const isDoc = /\.(doc|docx)$/i.test(pub.upload_document);
                console.log(
                  "pub id",
                  pub.user,
                  "current user id",
                  currentUserId
                );

                return (
                  <div className="relative bg-white border border-gray-200 rounded-xl p-5 mb-4 shadow-sm hover:-translate-y-1 transition-transform" key={index}>
                   {editMode && editPublicationId === pub.id && (
  <div className="mt-5 mb-5 p-6 border-2 border-dashed border-slate-300 rounded-xl bg-slate-50 flex flex-col gap-4">
    <input
      type="text"
      value={editTitle}
      onChange={(e) => setEditTitle(e.target.value)}
      placeholder="Edit title"
      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
    />
    <textarea
      value={editAbstract}
      onChange={(e) => setEditAbstract(e.target.value)}
      placeholder="Edit abstract"
      className="w-full min-h-[100px] px-4 py-3 border border-gray-300 rounded-xl text-sm bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 resize-y transition-all"
    />
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-2">Sharing Options</label>
      <select
        value={editVisibility}
        onChange={(e) => setEditVisibility(e.target.value)}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm bg-white text-gray-900 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all appearance-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2390A3BF' stroke-width='1.5'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 16px center'
        }}
      >
        <option value="public">Public</option>
        <option value="private">Private</option>
      </select>
    </div>
    <div className="flex gap-3">
      <button
        className="flex-1 px-4 py-2.5 bg-[#0f6abf] text-white text-sm font-medium rounded-xl hover:bg-[#0a5ca8] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        onClick={handleEditSubmit}
        disabled={loading}
      >
        {loading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Saving...
          </>
        ) : (
          "Save Changes"
        )}
      </button>
      <button
        className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-100 transition-colors"
        onClick={() => {
          setEditMode(false);
          setEditPublicationId(null);
          setEditTitle("");
          setEditAbstract("");
        }}
      >
        Cancel
      </button>
    </div>
  </div>
)}

                    <PublicationCard
                      image={fileUrl}
                      heading={pub.title}
                      subtitle={isPdf || isDoc ? fileName : ""}
                      description={pub.abstract || pub.description || ""} // show abstract first
                      isPdf={isPdf || isDoc}
                      onPreview={() => handlePreview(pub)}
                      postId={pub.id}
                      is_Liked={pub.is_Liked}
                      author={pub.author}
                      collaborators={pub.collaborators || []}
                      likeCount={pub.total_likes}
                      commentCount={pub.total_comments}
                      currentUserId={currentUserId}
                      dropdownActions={
                        canUserEdit(pub) ? (
                          <div >
                            <DropdownActions
                              type="publication"
                              onEdit={() => handleEditClick(pub)}
                              onDelete={() => handleDeleteClick(pub.id)}
                              onInvite={() => setInvitePublicationId(pub.id)}
                            />
                          </div>
                        ) : null
                      }
                      onRemoveCollaborator={
                        canRemoveCollaborator(pub)
                          ? handleRemoveCollaborator
                          : null
                      }
                    />
                  </div>
                );
              })}

              <div ref={lastImageRef} style={{ height: 1 }} />
              {isFetchingNextPage && (
                <div className="loading-snippets">
                  <ClipLoader color="#4A90E2" size={40} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    {showDeleteModal && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
    <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Delete Publication</h3>
            <p className="text-sm text-gray-500 mt-0.5">This action cannot be undone</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <p className="text-gray-600 mb-2">
          Are you sure you want to delete this publication?
        </p>
        <p className="text-sm text-gray-500">
          All data associated with this publication will be permanently removed.
        </p>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100">
        <div className="flex items-center justify-end gap-3">
          <button
            className="px-5 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            className="px-5 py-2.5 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600 transition-colors flex items-center gap-2"
            onClick={confirmDelete}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete Publication
          </button>
        </div>
      </div>
    </div>
  </div>
)}

      {/* Invite Collaborator Modal */}
    
   {invitePublicationId && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
    <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
      {/* Modal Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Select Collaborator</h3>
            <p className="text-sm text-gray-500 mt-0.5">Invite someone to collaborate</p>
          </div>
        </div>
      </div>

      {/* User List */}
      <div className="p-6 max-h-64 overflow-y-auto">
        {followingList.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <p className="text-gray-500">You are not following anyone</p>
          </div>
        ) : (
          <div className="space-y-2">
            {followingList.map((user) => (
              <div
                key={user.id}
                onClick={() => {
                  handleInviteCollaborator(invitePublicationId, user.id);
                  setInvitePublicationId(null);
                }}
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="relative">
                  <img
                    src={user.profile_picture || fallbackImage}
                    alt={user.name}
                    className="w-10 h-10 rounded-lg object-cover border border-gray-200"
                  />
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">{user.name}</p>
                  <p className="text-xs text-gray-500">Online</p>
                </div>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cancel Button */}
      <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100">
        <button
          className="w-full py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
          onClick={() => setInvitePublicationId(null)}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default Publication;
